import { Link } from "react-router-dom";

interface SectionPageHeroProps {
  badge: string;
  headline: React.ReactNode;
  subheadline: string;
  imageUrl: string;
  primaryCta?: { label: string; href: string; tel?: boolean };
  secondaryCta?: { label: string; href: string };
  stats?: { value: string; label: string }[];
}

export default function SectionPageHero({
  badge,
  headline,
  subheadline,
  imageUrl,
  primaryCta = { label: "Call (817) 832-4968", href: "tel:8178324968", tel: true },
  secondaryCta = { label: "Request Service", href: "/contact" },
  stats,
}: SectionPageHeroProps) {
  return (
    <section className="relative min-h-[560px] flex items-end overflow-hidden pt-[100px]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          alt={badge}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1829]/90 via-[#0B1829]/70 to-[#0B1829]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1829]/80 via-transparent to-transparent" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 pb-14 pt-10">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#E8500A]/20 border border-[#E8500A]/40 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 bg-[#E8500A] rounded-full" />
            <span className="text-orange-300 text-xs font-bold tracking-widest uppercase">
              {badge}
            </span>
          </div>

          {/* Headline */}
          <h1
            className="text-4xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight mb-5"
            style={{ fontFamily: "'Barlow', sans-serif" }}
          >
            {headline}
          </h1>

          <p className="text-gray-300 text-lg leading-relaxed mb-8 max-w-2xl">
            {subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            {primaryCta.tel ? (
              <a
                href={primaryCta.href}
                className="flex items-center gap-2.5 bg-[#E8500A] hover:bg-[#d14508] text-white font-bold px-7 py-4 rounded-lg text-sm transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </span>
                {primaryCta.label}
              </a>
            ) : (
              <Link
                to={primaryCta.href}
                className="flex items-center gap-2.5 bg-[#E8500A] hover:bg-[#d14508] text-white font-bold px-7 py-4 rounded-lg text-sm transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                {primaryCta.label}
              </Link>
            )}
            <Link
              to={secondaryCta.href}
              className="flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-7 py-4 rounded-lg text-sm transition-all hover:bg-white/10 whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-check-line" />
              </span>
              {secondaryCta.label}
            </Link>
          </div>

          {/* Optional stats */}
          {stats && stats.length > 0 && (
            <div className="flex flex-wrap gap-8 mt-8 pt-7 border-t border-white/15">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <p
                    className="text-2xl font-black text-[#E8500A]"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-gray-400 text-xs font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
